/**
 * YOUTUBE (GOOGLE) CONNECT GATE — shown on /google-ads/youtube until a Google
 * account is linked AND a Google Ads customer account is chosen.
 *
 * OAuth is a full-tab redirect to Google's consent screen (the backend builds
 * the URL and handles the callback). This card also owns the second state —
 * connected but no customer picked — mirroring the TikTok advertiser picker.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import toast from 'react-hot-toast';
import { Check, ChevronLeft, ChevronRight, ExternalLink, MonitorPlay } from 'lucide-react';
import { googleAdsService } from '@/services/google-ads.service';
import { alreadyToasted, errMsg, Spinner, type YtAuthStatus } from './youtubeShared';

type YtCustomer = {
  customer_id: string;
  customer_name: string;
  currency?: string | null;
  is_manager?: boolean;
  /** ENABLED / CLOSED / CANCELED / SUSPENDED. Unreliable for test accounts:
   *  working test accounts report CLOSED — never block on it when is_test. */
  account_status?: string;
  is_test?: boolean;
};

export default function YouTubeConnectCard({ auth, accountUid, onChanged }: {
  auth: YtAuthStatus; accountUid: string; onChanged: () => void;
}) {
  const [launching, setLaunching] = useState(false);
  const [customers, setCustomers] = useState<YtCustomer[] | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Drill-in: a clicked manager and the client accounts under it. Campaigns
  // can't live in managers, and clients created inside one are invisible to
  // the top-level list — so manager rows open, they don't select.
  const [inManager, setInManager] = useState<YtCustomer | null>(null);
  const [clients, setClients] = useState<YtCustomer[] | null>(null);

  const needsCustomer = auth.connected && !auth.customer_selected && !!accountUid;

  const loadCustomers = useCallback(async () => {
    setError(null);
    try {
      const r = await googleAdsService.listAccessibleCustomers(accountUid);
      if (r.warning) setError(r.warning);
      const reachable: Array<{ customerId: string; name: string; currency?: string; isManager: boolean; isTest: boolean }> =
        r.customers ?? r.customerIds.map((id) => ({ customerId: id, name: `Account ${id}`, isManager: false, isTest: false }));
      setCustomers(reachable.map((c) => ({
        customer_id: c.customerId, customer_name: c.name, currency: c.currency, is_manager: c.isManager, is_test: c.isTest,
      })));
    } catch (e) {
      setError(errMsg(e, 'Could not read your Google Ads accounts.'));
      setCustomers([]);
    }
  }, [accountUid]);

  useEffect(() => { if (needsCustomer) loadCustomers(); }, [needsCustomer, loadCustomers]);

  const launch = async () => {
    setLaunching(true);
    setError(null);
    try {
      const r = await googleAdsService.oauthStart();
      if (!r.url) {
        setError('Google Ads credentials are not configured on the server.');
        return;
      }
      window.location.href = r.url;
    } catch (e) {
      setError(errMsg(e, 'Could not start the Google connection.'));
    } finally {
      setLaunching(false);
    }
  };

  const pick = async (c: YtCustomer, loginCustomerId?: string) => {
    setPicking(c.customer_id);
    try {
      const acc = await googleAdsService.attachCustomer(accountUid, c.customer_id, loginCustomerId);
      toast.success(acc?.currencyCode ? `Google Ads account connected — bills in ${acc.currencyCode}` : 'Google Ads account selected');
      onChanged();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`Could not select account — ${errMsg(e)}`);
    } finally {
      setPicking(null);
    }
  };

  const openManager = async (m: YtCustomer) => {
    setError(null);
    setInManager(m);
    setClients(null);
    try {
      const r = await googleAdsService.listManagerClients(accountUid, m.customer_id);
      setClients(r.clients.map((c) => ({
        customer_id: c.customerId, customer_name: c.name, currency: c.currency,
        is_manager: c.isManager, account_status: c.status, is_test: c.isTest,
      })));
    } catch (e) {
      setError(errMsg(e));
      setClients([]);
    }
  };

  return (
    <Card className="mx-auto mt-8 w-full max-w-xl p-8">
      <div className="text-center">
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gold-gradient text-on-primary shadow-glow">
          <MonitorPlay size={28} />
        </span>

        {!auth.connected && (
          <>
            <h2 className="text-lg font-semibold text-text-primary">Connect your Google Ads account</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-text-muted">
              You'll sign in with Google and authorize SlightorCRM to manage YouTube video
              campaigns on your Google Ads account.
            </p>
            <Button className="mt-5" onClick={launch} loading={launching} iconLeft={<ExternalLink size={16} />}>
              Connect Google Account
            </Button>
            <p className="mt-3 text-2xs text-text-muted">
              You'll be taken to Google and brought back here when done.
            </p>
          </>
        )}

        {needsCustomer && (
          <>
            <h2 className="text-lg font-semibold text-text-primary">Choose a Google Ads account</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-text-muted">
              Your Google login can access these accounts. Campaigns will be created under
              the one you pick — choose an ad account, not a manager (MCC).
            </p>

            {customers === null && (
              <div className="mt-5 flex items-center justify-center gap-2 text-sm text-text-muted">
                <Spinner size={16} /> Loading accounts…
              </div>
            )}

            {customers !== null && customers.length === 0 && !error && (
              <p className="mt-5 text-sm text-text-muted">
                No accounts were returned. Create one in Google Ads, then
                <button className="px-1 text-sm text-primary hover:underline" onClick={loadCustomers}>reload</button>.
              </p>
            )}

            {!inManager && (
              <div className="mt-5 space-y-2 text-left">
                {(customers ?? []).map((c) => (
                  <button
                    key={c.customer_id}
                    className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-surface-alt disabled:opacity-60"
                    disabled={picking !== null}
                    onClick={() => (c.is_manager ? openManager(c) : pick(c))}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-text-primary">
                        {c.customer_name}{c.is_manager ? ' (manager)' : ''}
                      </div>
                      <div className="text-2xs text-text-muted">
                        ID {c.customer_id}{c.currency ? ` · ${c.currency}` : ''}
                        {c.is_manager ? ' · click to see its accounts' : ''}
                      </div>
                    </div>
                    {picking === c.customer_id
                      ? <Spinner size={16} className="text-text-muted" />
                      : c.is_manager
                        ? <ChevronRight size={16} className="text-text-muted" />
                        : <Check size={16} className="text-text-muted" />}
                  </button>
                ))}
              </div>
            )}

            {inManager && (
              <div className="mt-5 text-left">
                <button
                  className="mb-2 flex items-center gap-1 text-xs text-text-muted hover:text-text-primary"
                  onClick={() => { setInManager(null); setClients(null); setError(null); }}
                >
                  <ChevronLeft size={14} /> All accounts
                </button>
                <p className="mb-2 text-xs text-text-muted">
                  Accounts under <span className="font-medium text-text-primary">{inManager.customer_name}</span>:
                </p>
                {clients === null && (
                  <div className="flex items-center gap-2 text-sm text-text-muted">
                    <Spinner size={16} /> Loading…
                  </div>
                )}
                {clients !== null && clients.length === 0 && !error && (
                  <p className="text-sm text-text-muted">No client accounts under this manager.</p>
                )}
                <div className="space-y-2">
                  {(clients ?? []).map((c) => {
                    // Google's status field reports CLOSED for perfectly working
                    // test accounts — a test account is always offered, and the
                    // campaign mutate is the real judge of whether it works.
                    const closed = !c.is_test && !!c.account_status && c.account_status !== 'ENABLED';
                    return (
                      <button
                        key={c.customer_id}
                        className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-surface-alt disabled:opacity-60"
                        disabled={picking !== null || c.is_manager || closed}
                        onClick={() => pick(c, inManager.customer_id)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-text-primary">
                            {c.customer_name}{c.is_manager ? ' (manager)' : ''}
                          </div>
                          <div className="text-2xs text-text-muted">
                            ID {c.customer_id}{c.currency ? ` · ${c.currency}` : ''}
                            {closed ? ` · ${c.account_status} — reactivate it in Google Ads to use it` : ''}
                          </div>
                        </div>
                        {picking === c.customer_id
                          ? <Spinner size={16} className="text-text-muted" />
                          : <Check size={16} className={closed ? 'text-text-muted/30' : 'text-text-muted'} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <Button variant="secondary" size="sm" className="mt-4" onClick={launch} loading={launching}>
              Reconnect with a different Google login
            </Button>
          </>
        )}

        {error && (
          <div className="mx-auto mt-5 max-w-md rounded-xl border border-danger/30 bg-danger-soft p-3 text-left">
            <p className="text-xs font-semibold text-danger">Google connection problem</p>
            <p className="mt-1 break-words text-xs text-text-muted">{error}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
